import React, { useMemo, JSX } from "react";
import { useSelector } from "react-redux";
import DecisionTrackerTable from "../components/DecisionTrackerTable";
import DecisionCard from "../components/Decisioncards";
import Topbar from "../components/Topbar";
import { getMockDecisionData } from "../components/mockDecisionData";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";

interface CustomTooltipProps {
  active?: any;
  payload?: any;
  label?: any;
  data?: any[];
}

const colorPalette = [
  '#831843', 
  '#E11D48', 
  '#0369A1',
  '#059669', 
  '#7C3AED', 
  '#DB2777', 
];

const generateChartData = (mockData: any[]) => {
  if (!mockData || mockData.length === 0) return [];

  const months = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
  return months.map(month => {
    const dataPoint: any = { name: month };
    mockData.forEach(product => {
      if (product[month]) {
        dataPoint[product.product] = product[month];
      }
    });
    return dataPoint;
  });
};

/**
 * Custom tooltip component for the chart
 * Receives data as prop instead of relying on closure
 */
const CustomTooltip = ({ active, payload, label, data }: CustomTooltipProps): any => {
  if (active && payload && payload.length && data) {
    const previousIndex = data.findIndex((item: any) => item.name === label) - 1;
    const previousLabel = previousIndex >= 0 ? data[previousIndex]?.name : null;

    return (
      <div
        style={{
          background: "#FFF",
          padding: "12px",
          borderRadius: "8px",
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          minWidth: 320,
        }}
      >
        <p style={{ fontWeight: "bold", marginBottom: "4px" }}>{label} 2025</p>
        {previousLabel && (
          <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "12px" }}>
            Compared to {previousLabel} 2025
          </p>
        )}
        {payload.map((entry: any, index: any) => {
          const previousValue = previousIndex >= 0 ? (data as any)[previousIndex]?.[entry.dataKey] : undefined;
          const isIncrease = previousValue !== undefined && entry.value > previousValue;
          const delta = previousValue !== undefined 
            ? Math.abs(entry.value - previousValue).toFixed(1) + '%' 
            : `${entry.value.toFixed(1)}%`;

          return (
            <div
              key={index}
              style={{ display: "flex", alignItems: "center", gap: "32px", marginBottom: "4px" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 120, maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <circle cx="6" cy="6" r="6" fill={entry.color} />
                </svg>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{entry.dataKey}</span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", width: '100%', justifyContent: 'flex-end' }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d={
                      previousValue === undefined
                        ? "M2 12H22" 
                        : isIncrease
                        ? "M22 7L13.5 15.5L8.5 10.5L2 17M22 7H16M22 7V13"
                        : "M22 17L13.5 8.5L8.5 13.5L2 7M22 17H16M22 17V11"
                    }
                    stroke={previousValue === undefined ? '#9CA3AF' : isIncrease ? "#16A34A" : "#DC2626"}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{delta}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};

const DecisionTrackerPage = (): JSX.Element => {
  const userEmail = useSelector((state: any) => state.auth?.user?.email);
  // For now show the same decision cards for both users
  const mockData = getMockDecisionData(userEmail);
  const isSam = userEmail?.trim()?.toLowerCase() === "sam@kimaru.ai";
  // Chart data and product names
  const data = useMemo(() => generateChartData(mockData), [mockData]);
  const productNames = useMemo(() => mockData.map(item => item.product), [mockData]);
  const lineComponents = useMemo(() => {
    return productNames.map((productName, index) => (
      <Line
        key={productName}
        type="linear"
        dataKey={productName}
        stroke={colorPalette[index % colorPalette.length]}
        strokeWidth={3}
        dot={false}
      />
    ));
  }, [productNames]);
  const chartDescription = "Month-over-month readout of product margin ";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="">
        <Topbar showGreeting={false} placeholderText={"6 Month (Mar-Aug 2025)"} />
      </div>
      <div className="flex flex-col gap-6 p-6 pt-0 mx-auto">
        {/* Decision Cards Section */}
        <div style={{ display: "flex", gap: "16px", overflowX: "auto", paddingBottom: "8px" }}>
          <DecisionCard
            title="Gross Margin (%)"
            unit=""
            value="18.2"
            value_type="percentage"
            value_change="-1.1"
            date="8/5"
            secondary_value="19.3"
            secondary_value_type="percentage"
            selected={true}
          />
          <DecisionCard
            title="Revenue (yen symbol)"
            unit="yen"
            value="12901344"
            value_type="number"
            value_change="3093579"
            date="8/5"
            secondary_value="15994500"
            secondary_value_type="number"
            selected={false}
          />
        </div>

        {/* Chart Section */}
        <div
          style={{
            display: "flex",
            padding: "24px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "12px",
            flex: "1 0 0",
            alignSelf: "stretch",
            borderRadius: "8px",
            border: "1px solid #E2E8F0",
            background: "#FFF",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          }}
        >
          <h2 style={{ fontWeight: "bold", fontSize: "18px", margin: 0 }}>
            Gross Margin (%)
          </h2>
          <p style={{ fontSize: "14px", color: "#64748b", margin: 0, marginBottom: "12px" }}>
            {chartDescription}
          </p>
          
          {data.length > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data as any} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid 
                  horizontal={true} 
                  vertical={false}
                  stroke="#E2E8F0"
                  strokeWidth={1}
                  opacity={0.8}
                />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis 
                  domain={isSam ? [65, 75] : [17, 21]}
                  tickFormatter={(value: any) => `${value}%`} 
                  axisLine={false} 
                  tickLine={false}
                />
                <Tooltip content={(props: any) => <CustomTooltip {...props} data={data} />} />
                <Legend
                  wrapperStyle={{ paddingTop: "20px" }}
                  content={(props: any) => {
                    const { payload } = props;
                    return (
                      <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap" }}>
                        {payload.map((entry: any, index: any) => (
                          <div 
                            key={index} 
                            style={{ 
                              display: "inline-flex", 
                              alignItems: "center", 
                              gap: "8px"
                            }}
                          >
                            <span
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "2px",
                                backgroundColor: entry.color,
                                display: "inline-block"
                              }}
                            ></span>
                            <span>{entry.value}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                {lineComponents}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Decision Tracker Table Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100">
          <div className="p-6">
            <DecisionTrackerTable chartData={mockData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionTrackerPage;
